/*
 * PROD-7828 A-22 W2 — Payment details review: pure response shaping, wording and error routing,
 * pinned to teacher_payment_details.json by adapters.test.js. Every value here is the server's masked
 * one; the full account number only ever passes through reveal_view, never through a list or a view.
 */
import { EM_DASH, api_error } from '../format';
import { reason_length } from '../needs_attention/params';

const str = (v) => (typeof v === 'string' ? v : '');
const shown = (v) => str(v) || EM_DASH;
const yes_no = (v) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return EM_DASH;
};
const pct = (v) => (str(v) ? `${str(v)}%` : EM_DASH);

export const CODE_NOT_PENDING = 'PAYMENT_DETAILS_NOT_PENDING';
export const CODE_SHARED_CONFIRM = 'SHARED_ACCOUNT_CONFIRM_REQUIRED';
export const CODE_NOT_FOUND = 'PAYMENT_DETAILS_NOT_FOUND';
export const REASON_MIN_LEN = 5;
export const REASON_MAX_LEN = 512;
export const REVEAL_SHOWN_MS = 30000;

export const STALE_TEXT =
  'These details were replaced by a newer submission or already reviewed. Reload the list to see the latest.';
export const FORBIDDEN_TEXT = 'Only the teacher payments team can see payment details.';

// holder_name_match (S1): a hint for Ops, never a refusal.
const NAME_MATCH = {
  MATCH: { label: 'Name matches profile', tone: 'green' },
  PARTIAL: { label: 'Name partly matches profile', tone: 'orange' },
  DIFFERENT: { label: 'Name differs from profile', tone: 'red' }
};

export const name_match_view = (code) =>
  NAME_MATCH[code] || { label: 'Name not checked', tone: 'gray' };

const ACCOUNT_TYPES = { SAVINGS: 'Savings', CURRENT: 'Current' };
const STATUSES = {
  PENDING: { label: 'Waiting for review', tone: 'orange' },
  APPROVED: { label: 'Approved', tone: 'green' },
  REJECTED: { label: 'Rejected', tone: 'red' },
  SUPERSEDED: { label: 'Replaced', tone: 'gray' }
};

export const status_view = (code) =>
  STATUSES[code] || { label: str(code) || EM_DASH, tone: 'gray' };

const teacher_of = (t) => ({
  teacher_uuid: str(t && t.teacher_uuid),
  teacher_name: str(t && t.teacher_name) || EM_DASH,
  teacher_email: str(t && t.teacher_email)
});

// GET /payout_details/pending: oldest first; next_after_id null = the last page.
export const pending_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    ...teacher_of(r),
    detail_uuid: str(r.detail_uuid),
    submitted_at: shown(r.submitted_at_ist),
    account_holder_name: shown(r.account_holder_name),
    account_number_masked: shown(r.account_number_masked),
    ifsc: shown(r.ifsc),
    bank_name: shown(r.bank_name),
    gst_registered: r.gst_registered === true,
    gst_rate: pct(r.gst_rate_pct),
    tds_rate: pct(r.tds_rate_pct),
    is_change: r.is_change === true,
    shared_account: r.shared_account === true,
    name_match: name_match_view(r.holder_name_match)
  }));
  const next = typeof d.next_after_id === 'number' && d.next_after_id > 0 ? d.next_after_id : null;
  return { rows, next_after_id: next };
};

// The compared fields, in reading order; `changed` is the key changed_fields uses (null = not compared).
const FIELDS = [
  { key: 'account_holder_name', changed: 'account_holder_name', label: 'Account holder name' },
  { key: 'bank_file_name', changed: null, label: 'Name in the bank file' },
  { key: 'account_number_masked', changed: 'account_number', label: 'Account number' },
  { key: 'ifsc', changed: 'ifsc', label: 'IFSC' },
  { key: 'bank', changed: null, label: 'Bank and branch' },
  { key: 'account_type', changed: 'account_type', label: 'Account type' },
  { key: 'pan_masked', changed: 'pan', label: 'PAN' },
  { key: 'pan_aadhaar_linked', changed: 'pan_aadhaar_linked', label: 'PAN linked to Aadhaar' },
  { key: 'gst_registered', changed: 'gst_registered', label: 'GST registered' },
  { key: 'gstin_masked', changed: 'gstin', label: 'GSTIN' },
  { key: 'takes_input_credit', changed: 'takes_input_credit', label: 'Takes input tax credit' },
  { key: 'gst_rate_pct', changed: 'gst_rate_pct', label: 'GST rate' },
  { key: 'tds_rate_pct', changed: 'tds_rate_pct', label: 'TDS rate' }
];

// One side (submission or the details in use) as display strings; null side = no approved details.
const side_values = (d) => {
  if (!d) return null;
  const bank = [str(d.bank_name), str(d.branch)].filter(Boolean).join(', ');
  return {
    account_holder_name: shown(d.account_holder_name),
    bank_file_name: shown(d.bank_file_name),
    account_number_masked: shown(d.account_number_masked),
    ifsc: shown(d.ifsc),
    bank: bank || EM_DASH,
    account_type: ACCOUNT_TYPES[d.account_type] || shown(d.account_type),
    pan_masked: shown(d.pan_masked),
    pan_aadhaar_linked: yes_no(d.pan_aadhaar_linked),
    gst_registered: yes_no(d.gst_registered),
    gstin_masked: shown(d.gstin_masked),
    takes_input_credit: yes_no(d.takes_input_credit),
    gst_rate_pct: pct(d.gst_rate_pct),
    tds_rate_pct: pct(d.tds_rate_pct)
  };
};

// GET /payout_details/view: field rows side by side, the changed ones flagged for highlighting.
export const detail_view = (data) => {
  const d = data || {};
  const sub = d.submission || {};
  const cur = d.current_approved || null;
  const changed = new Set(Array.isArray(d.changed_fields) ? d.changed_fields : []);
  const sub_vals = side_values(sub);
  const cur_vals = side_values(cur);
  const shared = (Array.isArray(d.shared_with) ? d.shared_with : []).map((t) => ({
    teacher_uuid: str(t && t.teacher_uuid),
    teacher_name: str(t && t.teacher_name) || EM_DASH
  }));
  return {
    teacher: teacher_of(d.teacher),
    detail_uuid: str(sub.uuid),
    ifsc: str(sub.ifsc),
    status: status_view(sub.status),
    submitted_at: shown(sub.submitted_at_ist),
    reviewed_at: str(sub.reviewed_at_ist),
    review_note: str(sub.review_note),
    has_current: cur !== null,
    current_uuid: cur ? str(cur.uuid) : '',
    rows: FIELDS.map((f) => ({
      key: f.key,
      label: f.label,
      submission: sub_vals[f.key],
      current: cur_vals ? cur_vals[f.key] : EM_DASH,
      changed: cur !== null && f.changed !== null && changed.has(f.changed)
    })),
    changed_count: cur === null ? 0 : changed.size,
    name_match: name_match_view(d.holder_name_match),
    shared_with: shared,
    can_review: d.can_review === true
  };
};

export const IFSC_UNAVAILABLE_TEXT = 'Branch lookup unavailable';
const IFSC_NOT_FOUND_TEXT = 'IFSC not found in the bank directory';
const IFSC_PREFIX_TEXT = 'Branch not available; bank read from the IFSC prefix';

// GET /payout_details/ifsc: a hint for Ops next to the submitted IFSC, never a gate. source 'prefix' = Razorpay
// was down, so only the bank name (when the prefix is known) is shown.
export const ifsc_hint_view = (data) => {
  const d = data || {};
  if (d.found === false) return { text: IFSC_NOT_FOUND_TEXT, warn: true };
  const place = [str(d.branch), str(d.city), str(d.state)].filter(Boolean).join(', ');
  const text = [str(d.bank), place].filter(Boolean).join(' · ');
  if (d.source === 'prefix') {
    return { text: text ? `${text} (${IFSC_PREFIX_TEXT})` : IFSC_PREFIX_TEXT, warn: false };
  }
  return text ? { text, warn: false } : { text: IFSC_UNAVAILABLE_TEXT, warn: true };
};

// The number is held only for as long as it is on screen (REVEAL_SHOWN_MS), then dropped.
export const reveal_view = (data) => ({ account_number: str(data && data.account_number) });

// GET /payout_details/awaiting.
export const awaiting_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    ...teacher_of(r),
    latest_month: shown(r.latest_month),
    pending_detail_uuid: str(r.pending_detail_uuid)
  }));
  const next =
    typeof d.next_after_teacher_id === 'number' && d.next_after_teacher_id > 0
      ? d.next_after_teacher_id
      : null;
  return { since_month: str(d.since_month), rows, next_after_teacher_id: next };
};

// POST approve: the follow-up the toast states (recalc_months: OPEN months flagged after a rate change).
export const approve_done_text = (data, teacher_name) => {
  const months = Array.isArray(data && data.recalc_months) ? data.recalc_months : [];
  const who = teacher_name && teacher_name !== EM_DASH ? ` for ${teacher_name}` : '';
  const tail = months.length
    ? ` The new tax rates apply from now; ${months.join(', ')} will be recalculated.`
    : '';
  return `Payment details approved${who}.${tail}`;
};

export const reject_done_text = (teacher_name) =>
  teacher_name && teacher_name !== EM_DASH
    ? `Payment details rejected for ${teacher_name}. The teacher sees your reason.`
    : 'Payment details rejected. The teacher sees your reason.';

export const reject_reason_error = (raw) => {
  const n = reason_length(raw);
  if (n === 0) return 'Write the reason the teacher will see.';
  if (n < REASON_MIN_LEN) return `The reason must be at least ${REASON_MIN_LEN} characters.`;
  if (n > REASON_MAX_LEN) return `The reason must be at most ${REASON_MAX_LEN} characters.`;
  return '';
};

/*
 * A failed read or review, routed here rather than by the shell (the shell turns any 403 into the
 * module's no-permission page, but a module user without a payments role is refused only this tab).
 * kind: 'forbidden' | 'stale' (409 not pending: reload) | 'shared' (confirm needed: re-read the view)
 * | 'not_found' | 'inline'.
 */
export const review_error = (e, fallback) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (status === 401) return { kind: 'auth', text: message || fallback };
  if (status === 403 || code === 'FORBIDDEN') {
    return { kind: 'forbidden', text: message || FORBIDDEN_TEXT };
  }
  if (code === CODE_NOT_PENDING) return { kind: 'stale', text: message || STALE_TEXT };
  if (code === CODE_SHARED_CONFIRM) return { kind: 'shared', text: message || fallback };
  if (code === CODE_NOT_FOUND) return { kind: 'not_found', text: message || fallback };
  return { kind: 'inline', text: message || fallback };
};

const SINCE_MONTH_RE = /^20[0-9]{2}-(0[1-9]|1[0-2])$/;

// '' = the server's default; anything else must look like 2026-10 (the server's INVALID_PAY_MONTH).
export const since_month_error = (raw) => {
  const v = str(raw).trim();
  if (!v || SINCE_MONTH_RE.test(v)) return '';
  return 'Enter the month as YYYY-MM, for example 2026-10.';
};
